import { Box, SxProps, Typography } from '@mui/material';
import CheckIcon from 'app/components/Icons/CheckIcon';

interface Step {
  number: number;
  label: string;
  path: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  sx?: SxProps;
}

const Stepper = ({ steps, currentStep, sx }: StepperProps) => {
  const getStepState = (stepNumber: number) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';

    return 'pending';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0,
        mb: 4,
        overflowX: { xs: 'auto', sm: 'visible' },
        WebkitOverflowScrolling: 'touch',
        pb: { xs: 1, sm: 0 },
        mx: { xs: -1, sm: 0 },
        px: { xs: 1, sm: 0 },
        ...sx,
      }}
    >
      {steps.map((step, index) => {
        const state = getStepState(step.number);
        const isCompleted = state === 'completed';
        const isActive = state === 'active';
        const isPending = state === 'pending';

        return (
          <Box
            key={step.number}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flex: { xs: '0 0 132px', sm: 1 },
              minWidth: { xs: '132px', sm: 0 },
              opacity: isPending ? 0.4 : 1,
            }}
          >
            {/* Step Circle and Label */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* Top row: Circle and Line */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.25, sm: 0.5 },
                  width: '100%',
                }}
              >
                {/* Circle */}
                <Box
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isCompleted ? '#1cad62' : 'transparent',
                    border: isCompleted
                      ? 'none'
                      : isActive
                        ? '2px solid #1cad62'
                        : '1.5px solid #515555',
                    flexShrink: 0,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isCompleted ? (
                    <CheckIcon sx={{ width: '16px', height: '16px' }} />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#1cad62' : '#515555',
                        lineHeight: '20px',
                      }}
                    >
                      {step.number}
                    </Typography>
                  )}
                </Box>

                {/* Connecting Line */}
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      height: '1px',
                      bgcolor: '#dfe1e1',
                      transition: 'background-color 0.3s ease',
                      minWidth: { xs: '16px', sm: 'auto' },
                    }}
                  />
                )}
              </Box>

              {/* Label */}
              <Box
                sx={{
                  pr: 1.5,
                  maxWidth: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '12px', sm: '14px' },
                    fontWeight: 500,
                    color: isCompleted || isActive ? '#1c1f1f' : '#515555',
                    lineHeight: { xs: '16px', sm: '20px' },
                    wordBreak: 'break-word',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default Stepper;
